import { useEffect, useState } from 'react'
import {
  type QueueStatus,
  flushQueue,
  getQueueStatus,
  refreshQueueCounts,
  subscribeQueueStatus,
} from '@/lib/offline-queue'
import { subscribeConnectivity } from '@/lib/connectivity'

/** First retry delay after a failed run. */
const BASE_DELAY_MS = 20_000
/** Ceiling for the backoff. A day offline should still retry regularly. */
const MAX_DELAY_MS = 5 * 60_000

/**
 * Delay before the next replay attempt.
 *
 * Exponential with jitter: a shop with several tills that all lose the same
 * connection must not have them all retry in lockstep when it returns, and a
 * long outage must not turn into a request every twenty seconds.
 *
 * Exported for tests — the bounds are the contract.
 */
export function nextReplayDelayMs(attempt: number): number {
  const backoff = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** Math.max(0, attempt))
  // Half to full backoff, so retries spread out instead of synchronising.
  return Math.round(backoff * (0.5 + Math.random() * 0.5))
}

/**
 * Drives the offline sale queue: replays on mount, when connectivity returns,
 * when the app becomes visible again (the only trigger Safari offers, since it
 * has no Background Sync), and on a backoff timer for the case where `online`
 * fires while the server is still unreachable.
 *
 * Mounted once inside the app shell. It never prompts and never blocks — a
 * failed replay is surfaced by `OfflineSalesStatus`, not by a toast storm.
 */
export function useOfflineSalesSync(): QueueStatus {
  const [status, setStatus] = useState<QueueStatus>({
    pending: 0,
    failed: 0,
    replaying: false,
    needsSignIn: false,
    // The queue holds repayments as well as sales; the split is what keeps the
    // banner from calling a payment a sale before the first refresh lands.
    pendingSales: 0,
    pendingPayments: 0,
    failedSales: 0,
    failedPayments: 0,
    held: 0,
  })

  useEffect(() => {
    const unsubscribe = subscribeQueueStatus(setStatus)

    void refreshQueueCounts()
    void flushQueue()

    // Connectivity, not `navigator.onLine`: the queue must also resume when the
    // server comes back while the device never went offline.
    let wasOffline = false
    const unsubscribeConnectivity = subscribeConnectivity((state) => {
      const recovered = wasOffline && state.online
      wasOffline = !state.online
      if (recovered) {
        void refreshQueueCounts()
        void flushQueue()
      }
    })

    // Returning to the app is the replay trigger on iOS. It is also what a shop
    // does after a connection drop, so it is worth doing everywhere.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      void refreshQueueCounts()
      void flushQueue()
    }
    document.addEventListener('visibilitychange', onVisible)

    // A fresh sign-in is the only thing that can clear a 401, so retry as soon
    // as one happens instead of waiting out the backoff.
    const onAuthChange = (event: Event) => {
      if ((event as CustomEvent<boolean>).detail === true) {
        void refreshQueueCounts()
        void flushQueue()
      }
    }
    window.addEventListener('authStateChange', onAuthChange)

    let timer: number | null = null
    let attempt = 0

    const scheduleNext = () => {
      if (timer !== null) window.clearTimeout(timer)
      timer = window.setTimeout(async () => {
        timer = null
        await flushQueue()

        const current = getQueueStatus()
        attempt = current.pending > 0 ? attempt + 1 : 0

        // A signed-out queue cannot be fixed by waiting; the auth listener
        // resumes it. Everything else keeps retrying.
        if (!current.needsSignIn) scheduleNext()
      }, nextReplayDelayMs(attempt))
    }
    scheduleNext()

    return () => {
      unsubscribe()
      unsubscribeConnectivity()
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('authStateChange', onAuthChange)
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [])

  return status
}
